/**
 * Quote estimator — step flow, validation and tracking.
 *
 * Markup: views/components/quote-estimator/*.twig
 * Math:   ./estimate.js
 */

import {
  createEstimator,
  GOAL_INCOME_LATER,
  GOAL_ACCUMULATION,
  SPECIALIST_REASON_AGE,
} from './estimate.js';
import { renderResults, fill } from './render-results.js';
import { reportToBugsnag } from '../../utils/error-reporting.js';

const STEP_GOAL = 'goal';
const STEP_AMOUNT = 'amount';
const STEP_AGE = 'age';
const STEP_AGEGATE = 'agegate';
const STEP_TIMING = 'timing';
const STEP_CONTACT = 'contact';
const STEP_RESULTS = 'results';

/**
 * @param {?string} goal
 * @returns {string[]} The steps this path visits, in order.
 */
function stepsFor(goal) {
  /* Assume the longest path until a goal is chosen, so the step total can only shrink.
     A growing total moves the finish line away from the visitor mid-funnel. */
  const questions = !goal || goal === GOAL_INCOME_LATER
    ? [STEP_AMOUNT, STEP_AGE, STEP_TIMING]
    : [STEP_AMOUNT, STEP_AGE];

  return [STEP_GOAL, ...questions, STEP_CONTACT, STEP_RESULTS];
}

/**
 * Wires one estimator instance.
 *
 * @param {HTMLElement} root The `.js-quote-estimator` section.
 * @returns {void}
 */
function mount(root) {
  const configScript = root.querySelector('.js-qe-config');

  if (!configScript) {
    return;
  }

  let estimator;
  try {
    estimator = createEstimator(JSON.parse(configScript.textContent));
  } catch (error) {
    reportToBugsnag(error, 'error', 'quote-estimator-config');
    return;
  }

  const { config } = estimator;
  const { strings, guardrails } = config;
  // PHP formats it once so the results screen and the disclosures cannot disagree.
  const asOfDisplay = root.dataset.asOfDisplay || config.as_of_date;

  const steps = new Map(
    [...root.querySelectorAll('.js-qe-step')].map((step) => [step.dataset.step, step])
  );
  const liveRegion = root.querySelector('.js-qe-live');
  const printButton = root.querySelector('.js-qe-print');
  const disclosures = [...root.querySelectorAll('.js-qe-disclosures')];
  const answerStrip = root.querySelector('.js-qe-answers');
  const progress = {
    wrapper: root.querySelector('.js-qe-progress'),
    label: root.querySelector('.js-qe-progress-label'),
    note: root.querySelector('.js-qe-progress-note'),
    bar: root.querySelector('.js-qe-progress-bar'),
    track: root.querySelector('[role="progressbar"]'),
  };

  const answers = {
    goal: null,
    bucketId: null,
    age: null,
    startAge: null,
    forcedByAgeGate: false,
  };

  let stepHistory = [STEP_GOAL];
  /* Computed when the contact step opens, so the figures written onto the lead are the same
     object the results screen renders. Recomputing after submit could disagree with it — the
     rate feed can cross a staleness boundary mid-session. */
  let pendingEstimate = null;
  let contactPrepared = false;

  const currentStep = () => stepHistory[stepHistory.length - 1];
  const sequenceFor = () => stepsFor(answers.goal);
  // The agegate interrupts the age step, so it counts as that step in the sequence.
  const stepIndexIn = (sequence, step) =>
    sequence.indexOf(step === STEP_AGEGATE ? STEP_AGE : step);

  /**
   * @param {string} action One of the house Form actions ('Step' for a step view).
   * @param {Object} [extra] Extra props. Never pass anything that identifies the visitor.
   * @returns {void}
   */
  function track(action, extra = {}) {
    if (typeof window.customEvent !== 'function') {
      return;
    }

    window.customEvent('Form', {
      action,
      step: Math.max(1, stepIndexIn(sequenceFor(), currentStep()) + 1),
      location: root.dataset.location || 'quote-estimator',
      offer: 'quote-estimator',
      layout: root.dataset.layout,
      goal: answers.goal || '',
      bucket_id: answers.bucketId || '',
      microIgnore: false,
      nonInteraction: false,
      ...extra,
    });
  }

  function updateProgress(step) {
    const sequence = sequenceFor();
    const index = Math.max(0, stepIndexIn(sequence, step));
    const total = sequence.length;

    // The form adapter reads these live, for Rover's impressions and the inspector tooltips.
    steps.forEach((element, name) => {
      element.dataset.trackStep = String(stepIndexIn(sequence, name) + 1);
      element.dataset.trackTotalsteps = String(total);
    });

    if (step === STEP_RESULTS) {
      progress.wrapper.hidden = true;
      return;
    }

    progress.wrapper.hidden = false;

    const remaining = total - (index + 1);
    const percent = Math.round((index / total) * 100);

    progress.label.textContent = answers.goal
      ? fill(strings.progress.label, { step: index + 1, total })
      : fill(strings.progress.label_unknown, { step: index + 1 });
    // Do not derive this from the remaining step count: these are single-tap questions, so it
    // then announces a minute per tap and overstates the whole funnel.
    progress.note.textContent =
      remaining <= 1 ? strings.progress.almost_done : strings.progress.estimate_minutes;
    progress.bar.style.width = `${Math.max(6, percent)}%`;
    progress.track.setAttribute('aria-valuenow', String(percent));
  }

  /**
   * The answers given so far, each with a way back to the step that set it.
   *
   * The funnel otherwise never shows a choice back to the visitor, so a mis-tap is invisible
   * until the results contradict what they meant.
   *
   * @param {string} step The step about to be shown.
   * @returns {void}
   */
  function renderAnswers(step) {
    const sequence = sequenceFor();
    const upToHere = stepIndexIn(sequence, step);
    const goalOption = strings.step_goal.options.find((option) => option.value === answers.goal);
    const bucket = answers.bucketId ? estimator.bucketById(answers.bucketId) : null;

    const given = [
      { step: STEP_GOAL, value: goalOption ? goalOption.title : null },
      { step: STEP_AMOUNT, value: bucket ? bucket.label : null },
      { step: STEP_AGE, value: answers.age ? `Age ${answers.age}` : null },
      { step: STEP_TIMING, value: answers.startAge ? `Income at ${answers.startAge}` : null },
    ].filter(
      (entry) => entry.value && sequence.includes(entry.step) && sequence.indexOf(entry.step) < upToHere
    );

    answerStrip.querySelectorAll('.js-qe-answer').forEach((chip) => chip.remove());

    given.forEach((entry) => {
      const chip = document.createElement('span');
      chip.className =
        'js-qe-answer tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-primary-tint ' +
        'tw-py-1 tw-px-3 tw-text-sm tw-text-dark-grey';
      chip.textContent = entry.value;

      const changeButton = document.createElement('button');
      changeButton.type = 'button';
      changeButton.className =
        'js-qe-change tw-bg-transparent tw-border-0 tw-p-0 tw-text-sm tw-text-primary tw-underline tw-cursor-pointer';
      changeButton.textContent = strings.change_answer;
      changeButton.dataset.target = entry.step;
      changeButton.setAttribute('aria-label', `${strings.change_answer}: ${entry.value}`);

      chip.appendChild(changeButton);
      answerStrip.appendChild(chip);
    });

    answerStrip.hidden = given.length === 0 || step === STEP_RESULTS;
  }

  function show(step) {
    steps.forEach((element, name) => {
      element.hidden = name !== step;
    });

    renderAnswers(step);

    const active = steps.get(step);
    if (active) {
      active.focus({ preventScroll: true });
      const announced = active.querySelector('.js-qe-announce');
      liveRegion.textContent = announced ? announced.textContent.trim() : '';
    }

    updateProgress(step);
  }

  function go(step) {
    if (currentStep() !== step) {
      stepHistory.push(step);
    }

    if (step === STEP_RESULTS) {
      showResults();
      return;
    }

    if (step === STEP_CONTACT) {
      prepareContactStep();
    }

    show(step);
    track('Step', { label: step });
  }

  function back() {
    if (stepHistory.length > 1) {
      stepHistory.pop();
    }

    // Leaving the guardrail must clear the flag it set, or the specialist screen sticks.
    answers.forcedByAgeGate = false;
    show(currentStep());
    track('Step', { label: currentStep(), direction: 'back' });
  }

  function showResults() {
    const estimate = pendingEstimate || estimator.estimate(answers);

    steps.get(STEP_RESULTS).querySelector('.js-qe-results-body').innerHTML = renderResults({
      estimate,
      estimator,
      answers,
      asOfDisplay,
    });

    if (printButton) {
      printButton.hidden = !estimate.showsNumbers;
    }

    show(STEP_RESULTS);
    track('Success', {
      label: STEP_RESULTS,
      shows_numbers: estimate.showsNumbers,
      specialist_reason: estimate.specialistReason || '',
      config_version: estimate.configVersion,
    });
  }

  /**
   * Fills one of the Fluent Form's hidden inputs, if the form declares it.
   *
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string|number} value
   * @returns {void}
   */
  function setHiddenField(form, name, value) {
    const field = form.querySelector(`input[name="${name}"]`);

    if (field) {
      field.value = String(value);
    }
  }

  /**
   * What the visitor was shown, for the advisor who calls them.
   *
   * Carries only what has no named field of its own. FormParser drops anything off its
   * allowlist, and `comments` is the allowlisted field these extras have to travel in.
   *
   * @param {Object} estimate
   * @returns {string}
   */
  function buildComments(estimate) {
    // Must match QUOTE_ESTIMATOR_COMMENTS_MARKER in functions/quote-estimator.php.
    const lines = ['Quote estimator', `type: ${estimate.goal}`];

    if (estimate.showsNumbers) {
      lines.push(
        estimate.goal === GOAL_ACCUMULATION
          ? `shown: ${estimate.estimate.terms
              .map((row) => `${row.term}yr ${row.low}-${row.high}`)
              .join(', ')}`
          : `shown: ${estimate.estimate.low}-${estimate.estimate.high}/mo`
      );
      lines.push(`on about ${estimate.premiumBasis}`);
    } else {
      lines.push(`no estimate shown (${estimate.specialistReason})`);
    }

    lines.push(`rates as of ${estimate.asOfDate}`);

    return lines.join(' | ');
  }

  /** @param {Object} estimate @returns {number|string} */
  function quoteAmountFor(estimate) {
    if (!estimate.showsNumbers) {
      return '';
    }
    if (estimate.goal !== GOAL_ACCUMULATION) {
      return estimate.estimate.low;
    }

    const { terms } = estimate.estimate;

    return (terms.find((row) => row.emphasis) || terms[0]).low;
  }

  /**
   * Opens the contact step: computes the estimate, writes it onto the form, and moves the
   * consent copy against the submit button.
   *
   * @returns {void}
   */
  function prepareContactStep() {
    const step = steps.get(STEP_CONTACT);
    const form = step.querySelector('form');

    pendingEstimate = estimator.estimate(answers);

    if (!form) {
      return;
    }

    const goalOption = strings.step_goal.options.find((option) => option.value === answers.goal);
    const bucket = estimator.bucketById(answers.bucketId);

    setHiddenField(form, 'qualifying_question', strings.step_goal.question);
    setHiddenField(form, 'qualifying_answer', goalOption ? goalOption.title : answers.goal);
    setHiddenField(form, 'investment_amount', bucket ? bucket.midpoint : '');
    setHiddenField(form, 'age', answers.age ?? '');
    setHiddenField(form, 'quote_amount', quoteAmountFor(pendingEstimate));
    setHiddenField(form, 'comments', buildComments(pendingEstimate));

    const asksTiming = sequenceFor().includes(STEP_TIMING) && answers.startAge !== null;

    setHiddenField(form, 'form_question_1', asksTiming ? strings.step_timing.question : '');
    setHiddenField(form, 'form_answer_1', asksTiming ? answers.startAge : '');

    if (contactPrepared) {
      return;
    }
    contactPrepared = true;

    // Consent must render against the button whose label it quotes.
    const submitButton = form.querySelector('.ff-btn-submit');
    const anchor = submitButton && (submitButton.closest('.ff-el-group') || submitButton);
    const tcpa = step.querySelector('.js-qe-tcpa-source');
    const privacyLine = step.querySelector('.js-qe-privacy-source');

    if (anchor && tcpa) {
      tcpa.hidden = false;
      anchor.parentNode.insertBefore(tcpa, anchor);
    }
    if (anchor && privacyLine) {
      privacyLine.hidden = false;
      anchor.parentNode.insertBefore(privacyLine, anchor.nextSibling);
    }

    if (window.jQuery) {
      window.jQuery(form).on('fluentform_submission_success', () => {
        go(STEP_RESULTS);
      });
    }

    form.addEventListener('submit', () => {
      track('Submit', { label: STEP_CONTACT, shows_numbers: pendingEstimate.showsNumbers });
    });
  }

  /** The step that follows `from`, applying the under-55 income guardrail. */
  function nextStepAfter(from) {
    const sequence = sequenceFor();
    const next = sequence[sequence.indexOf(from) + 1];

    if (
      from === STEP_AGE &&
      answers.goal !== GOAL_ACCUMULATION &&
      answers.age < guardrails.min_age_income
    ) {
      return STEP_AGEGATE;
    }

    if (
      from === STEP_AGE &&
      answers.goal === GOAL_INCOME_LATER &&
      answers.age >= guardrails.start_age_max
    ) {
      return STEP_CONTACT;
    }

    return next || STEP_RESULTS;
  }

  /**
   * Records a card selection.
   *
   * @param {HTMLInputElement} input
   * @returns {void}
   */
  function selectOption(input) {
    const step = input.closest('.js-qe-step');

    input.checked = true;

    if (step.dataset.step === STEP_GOAL) {
      answers.goal = input.value;
    } else {
      answers.bucketId = input.value;
    }

    step.querySelector('.js-qe-error').hidden = true;
    updateProgress(currentStep());
  }

  function readAge(step) {
    const input = step.querySelector('.js-qe-number');
    const value = Number.parseInt(input.value, 10);
    const errorMessage = step.querySelector('.js-qe-error');
    const isAgeStep = step.dataset.step === STEP_AGE;
    const valid = isAgeStep
      ? Number.isInteger(value) &&
        value >= guardrails.age_entry_min &&
        value <= guardrails.age_entry_max
      : Number.isInteger(value) && value > answers.age && value <= guardrails.start_age_max;

    errorMessage.hidden = valid;
    input.setAttribute('aria-invalid', valid ? 'false' : 'true');

    if (!valid) {
      input.focus();
      track('Error', { label: step.dataset.step });
      return null;
    }

    return value;
  }

  function updateTimingHint() {
    const step = steps.get(STEP_TIMING);
    const input = step.querySelector('.js-qe-number');
    const hint = step.querySelector('.js-qe-hint');
    const startAge = Number.parseInt(input.value, 10);
    const years = startAge - answers.age;

    if (!Number.isInteger(years) || years <= 0) {
      hint.textContent = '';
      return;
    }

    let text = fill(strings.step_timing.hint_years, {
      years,
      years_plural: years === 1 ? '' : 's',
    });

    if (years > config.income.max_deferral_years) {
      text += ` ${fill(strings.step_timing.hint_capped, {
        max_deferral_years: config.income.max_deferral_years,
      })}`;
    }

    hint.textContent = text;
  }

  function prepareTimingStep() {
    const input = steps.get(STEP_TIMING).querySelector('.js-qe-number');

    input.min = String(answers.age + 1);
    if (Number.parseInt(input.value, 10) <= answers.age) {
      input.value = String(
        Math.min(guardrails.start_age_max, answers.age + config.defaults.start_age_offset)
      );
    }
    updateTimingHint();
  }

  // `click` alone misses keyboard arrow selection, which fires only `change`.
  root.addEventListener('change', (event) => {
    if (event.target.closest('.js-qe-option')) {
      selectOption(event.target);
    }
  });

  root.addEventListener('click', (event) => {
    const step = event.target.closest('.js-qe-step');

    // `change` alone strands anyone who goes Back and re-picks the same answer: clicking an
    // already-checked radio fires `click` and no `change`.
    const option = event.target.closest('.js-qe-option');
    if (option) {
      selectOption(option.querySelector('input'));
      return;
    }

    if (event.target.closest('.js-qe-back')) {
      back();
      return;
    }

    if (event.target.closest('.js-qe-print')) {
      track('Click', { label: 'print' });
      window.print();
      return;
    }

    const changeButton = event.target.closest('.js-qe-change');
    if (changeButton) {
      const target = changeButton.dataset.target;
      const cutIndex = stepHistory.indexOf(target);
      stepHistory = cutIndex > -1 ? stepHistory.slice(0, cutIndex + 1) : [target];
      answers.forcedByAgeGate = false;
      show(target);
      track('Step', { label: target, direction: 'change' });
      return;
    }

    if (event.target.closest('.js-qe-agegate-switch')) {
      // Route through the radio so the goal step, the answer chip and the step total agree.
      const accumulation = steps
        .get(STEP_GOAL)
        .querySelector(`input[value="${GOAL_ACCUMULATION}"]`);
      if (accumulation) {
        selectOption(accumulation);
      } else {
        answers.goal = GOAL_ACCUMULATION;
      }
      answers.forcedByAgeGate = false;
      go(STEP_CONTACT);
      return;
    }

    if (event.target.closest('.js-qe-agegate-specialist')) {
      answers.forcedByAgeGate = true;
      track('Skip', { label: STEP_AGEGATE, specialist_reason: SPECIALIST_REASON_AGE });
      go(STEP_CONTACT);
      return;
    }

    const stepper = event.target.closest('.js-qe-step-up, .js-qe-step-down');
    if (stepper && step) {
      const input = step.querySelector('input[type="number"]');
      const delta = stepper.classList.contains('js-qe-step-up') ? 1 : -1;
      const min = Number(input.min);
      const max = Number(input.max);
      const next = Number.parseInt(input.value, 10) + delta;

      input.value = String(Math.min(max, Math.max(min, next)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (!event.target.closest('.js-qe-advance') || !step) {
      return;
    }

    const stepName = step.dataset.step;

    if (stepName === STEP_AGE) {
      const age = readAge(step);
      if (age === null) {
        return;
      }
      answers.age = age;

      const next = nextStepAfter(STEP_AGE);
      if (next === STEP_TIMING) {
        prepareTimingStep();
      }
      if (next === STEP_AGEGATE) {
        steps.get(STEP_AGEGATE).querySelector('.js-qe-agegate-body').textContent = fill(
          strings.step_agegate.sub,
          { age }
        );
      }
      go(next);
      return;
    }

    if (stepName === STEP_TIMING) {
      const startAge = readAge(step);
      if (startAge === null) {
        return;
      }
      answers.startAge = startAge;
      go(nextStepAfter(STEP_TIMING));
      return;
    }

    const chosen = stepName === STEP_GOAL ? answers.goal : answers.bucketId;

    if (!chosen) {
      const error = step.querySelector('.js-qe-error');
      error.hidden = false;
      step.querySelector('.js-qe-option input').focus();
      track('Error', { label: stepName });
      return;
    }

    go(nextStepAfter(stepName));
  });

  // Bound to `change`, not `input`: `input` fires on every keystroke of a typed age.
  steps.get(STEP_TIMING).querySelector('.js-qe-number').addEventListener('change', () => {
    steps.get(STEP_TIMING).querySelector('.js-qe-error').hidden = true;
    updateTimingHint();
  });

  steps.get(STEP_AGE).querySelector('.js-qe-number').addEventListener('change', () => {
    steps.get(STEP_AGE).querySelector('.js-qe-error').hidden = true;
  });

  const ancestors = [];
  for (let node = root.parentElement; node && node !== document.body; node = node.parentElement) {
    ancestors.push(node);
  }

  let disclosuresWereOpen = [];

  window.addEventListener('beforeprint', () => {
    document.body.classList.add('is-qe-printing');
    ancestors.forEach((node) => node.classList.add('is-qe-print-keep'));

    disclosuresWereOpen = disclosures.map((details) => details.open);
    disclosures.forEach((details) => {
      details.open = true;
    });
  });

  window.addEventListener('afterprint', () => {
    document.body.classList.remove('is-qe-printing');
    ancestors.forEach((node) => node.classList.remove('is-qe-print-keep'));

    disclosures.forEach((details, index) => {
      details.open = disclosuresWereOpen[index];
    });
  });

  updateProgress(currentStep());
  track('Step', { label: currentStep() });
}

// Mount after DOMContentLoaded: customEvent is an inline footer script, and a track() call
// made before it exists is silently dropped, taking the first step view with it.
const mountAll = () => document.querySelectorAll('.js-quote-estimator').forEach(mount);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll);
} else {
  mountAll();
}
